import React, { useEffect, useState } from "react";
import {
  Tab,
  Row,
  Col,
  Nav,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
} from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

import Topbar from "../../Layouts/Topbar";
import Header from "../../Layouts/Header";
import InnerBanner from "../../Layouts/InnerBanner";
import Footer from "../../Layouts/Footer";
import OrderList from "./OrderList";
import ChangePassword from "./ChangePassword";
import useAuthStore from "../../hooks/useAuthStore";
import { useNavigate } from "react-router-dom";
import { API } from "../../constants";

const Dashboard = () => {
  const navigate = useNavigate();

  const { user, clearUser, setUser } = useAuthStore();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [profileLoading, setProfileLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);

  const [message, setMessage] = useState({ type: "", text: "" });
  const [addressMessage, setAddressMessage] = useState({ type: "", text: "" });

  const [addressType, setAddressType] = useState("home");
  const [savedAddress, setSavedAddress] = useState(null);

  const defaultImage = "assets/images/user.jpeg";

  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return defaultImage;

    if (profileImage.startsWith("http")) {
      return profileImage;
    }

    return `${API.IMAGE_URL}uploads/customers/${profileImage}`;
  };

  const [profileImage, setProfileImage] = useState(
    getProfileImageUrl(user?.profileImage || user?.filePath),
  );

  const [form, setForm] = useState({
    phoneNumber: user?.phoneNumber || "",
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    birthday: user?.birthday || "",
    identity: user?.identity || "",
    married: user?.married || "",
  });

  const [addressForm, setAddressForm] = useState({
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    pinCode: "",
    city: "",
    state: "",
  });

  const isValidSavedAddress = (address) => {
    if (!address) return false;

    return Boolean(
      address.addressLine1?.trim() ||
      address.addressLine2?.trim() ||
      address.landmark?.trim() ||
      address.pinCode?.trim() ||
      address.city?.trim() ||
      address.state?.trim(),
    );
  };

  const fetchCustomerAddress = async () => {
    try {
      const response = await fetch(API.GET_CUSTOMER_ADDRESS, {
        method: "GET",
        credentials: "include",
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setSavedAddress(null);
        return;
      }

      const address = result.data || {};

      if (isValidSavedAddress(address)) {
        setSavedAddress(address);
        setAddressType(address.addressType || "home");

        setAddressForm({
          addressLine1: address.addressLine1 || "",
          addressLine2: address.addressLine2 || "",
          landmark: address.landmark || "",
          pinCode: address.pinCode || "",
          city: address.city || "",
          state: address.state || "",
        });
      } else {
        setSavedAddress(null);
        setAddressType("home");

        setAddressForm({
          addressLine1: "",
          addressLine2: "",
          landmark: "",
          pinCode: "",
          city: "",
          state: "",
        });
      }
    } catch (error) {
      console.error("Address fetch error:", error);
      setSavedAddress(null);
    }
  };

  useEffect(() => {
    setProfileImage(getProfileImageUrl(user?.profileImage || user?.filePath));

    setForm({
      phoneNumber: user?.phoneNumber || "",
      email: user?.email || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      birthday: user?.birthday || "",
      identity: user?.identity || "",
      married: user?.married || "",
    });

    fetchCustomerAddress();
  }, [user]);

  const handleLogoutFunction = async () => {
    try {
      await fetch(API.CUSTOMER_LOGOUT, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearUser();
      navigate("/login");
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const previewReader = new FileReader();

    previewReader.onload = (e) => {
      setProfileImage(e.target.result);
    };

    previewReader.readAsDataURL(file);

    try {
      setImageLoading(true);
      setMessage({ type: "", text: "" });

      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await fetch(API.UPDATE_CUSTOMER_PROFILE_IMAGE, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setMessage({
          type: "danger",
          text: result.message || "Failed to update profile image",
        });

        setProfileImage(
          getProfileImageUrl(user?.profileImage || user?.filePath),
        );
        return;
      }

      if (setUser) {
        setUser(result.data);
      }

      if (result.imageUrl) {
        setProfileImage(result.imageUrl);
      } else {
        setProfileImage(getProfileImageUrl(result.data?.profileImage));
      }

      setMessage({
        type: "success",
        text: result.message || "Profile image updated successfully",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        type: "danger",
        text: "Something went wrong while uploading image",
      });

      setProfileImage(getProfileImageUrl(user?.profileImage || user?.filePath));
    } finally {
      setImageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelect = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    setAddressForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAddressModal = () => {
    setAddressMessage({ type: "", text: "" });

    if (savedAddress) {
      setAddressType(savedAddress.addressType || "home");

      setAddressForm({
        addressLine1: savedAddress.addressLine1 || "",
        addressLine2: savedAddress.addressLine2 || "",
        landmark: savedAddress.landmark || "",
        pinCode: savedAddress.pinCode || "",
        city: savedAddress.city || "",
        state: savedAddress.state || "",
      });
    } else {
      setAddressType("home");

      setAddressForm({
        addressLine1: "",
        addressLine2: "",
        landmark: "",
        pinCode: "",
        city: "",
        state: "",
      });
    }

    setShowAddressModal(true);
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    setAddressMessage({ type: "", text: "" });

    if (!addressForm.addressLine1.trim()) {
      setAddressMessage({ type: "danger", text: "Address Line 1 is required" });
      return;
    }

    if (!addressForm.pinCode.trim()) {
      setAddressMessage({ type: "danger", text: "Pin Code is required" });
      return;
    }

    if (!addressForm.city.trim()) {
      setAddressMessage({ type: "danger", text: "City is required" });
      return;
    }

    if (!addressForm.state.trim()) {
      setAddressMessage({ type: "danger", text: "State is required" });
      return;
    }

    try {
      setAddressLoading(true);

      const payload = {
        addressType,
        addressLine1: addressForm.addressLine1,
        addressLine2: addressForm.addressLine2 || "",
        landmark: addressForm.landmark || "",
        pinCode: addressForm.pinCode,
        city: addressForm.city,
        state: addressForm.state,
      };

      const response = await fetch(API.UPDATE_CUSTOMER_ADDRESS, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setAddressMessage({
          type: "danger",
          text: result.message || "Failed to save address",
        });
        return;
      }

      if (setUser && result.data) {
        setUser(result.data);
      }

      const updatedAddress = result.data?.address || payload;

      setSavedAddress(updatedAddress);
      setAddressType(updatedAddress.addressType || "home");

      setAddressForm({
        addressLine1: updatedAddress.addressLine1 || "",
        addressLine2: updatedAddress.addressLine2 || "",
        landmark: updatedAddress.landmark || "",
        pinCode: updatedAddress.pinCode || "",
        city: updatedAddress.city || "",
        state: updatedAddress.state || "",
      });

      setAddressMessage({
        type: "success",
        text: result.message || "Address saved successfully",
      });

      setTimeout(() => {
        setShowAddressModal(false);
      }, 700);
    } catch (error) {
      console.error("Address save error:", error);

      setAddressMessage({
        type: "danger",
        text: "Something went wrong while saving address",
      });
    } finally {
      setAddressLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this address?",
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setAddressLoading(true);
      setMessage({ type: "", text: "" });

      const response = await fetch(API.DELETE_CUSTOMER_ADDRESS, {
        method: "DELETE",
        credentials: "include",
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setMessage({
          type: "danger",
          text: result.message || "Failed to delete address",
        });
        return;
      }

      if (setUser && result.data) {
        setUser(result.data);
      }

      setSavedAddress(null);
      setAddressType("home");

      setAddressForm({
        addressLine1: "",
        addressLine2: "",
        landmark: "",
        pinCode: "",
        city: "",
        state: "",
      });

      setMessage({
        type: "success",
        text: result.message || "Address deleted successfully",
      });
    } catch (error) {
      console.error("Address delete error:", error);

      setMessage({
        type: "danger",
        text: "Something went wrong while deleting address",
      });
    } finally {
      setAddressLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    setMessage({ type: "", text: "" });

    if (!form.firstName.trim()) {
      setMessage({ type: "danger", text: "First name is required" });
      return;
    }

    if (!form.lastName.trim()) {
      setMessage({ type: "danger", text: "Last name is required" });
      return;
    }

    if (!form.email.trim()) {
      setMessage({ type: "danger", text: "Email is required" });
      return;
    }

    if (!form.phoneNumber.trim()) {
      setMessage({ type: "danger", text: "Mobile number is required" });
      return;
    }

    try {
      setProfileLoading(true);

      const response = await fetch(API.UPDATE_CUSTOMER_PROFILE, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phoneNumber: form.phoneNumber,
          birthday: form.birthday || "",
          identity: form.identity || "",
          married: form.married || "",
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setMessage({
          type: "danger",
          text: result.message || "Failed to update profile",
        });
        return;
      }

      if (setUser) {
        setUser(result.data);
      }

      setMessage({
        type: "success",
        text: result.message || "Profile updated successfully",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        type: "danger",
        text: "Something went wrong while updating profile",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <>
      <Topbar />
      <Header />
      <InnerBanner />

      <section className="dashboard cmn-gap">
        <div className="container">
          <Tab.Container id="left-tabs-example" defaultActiveKey="profile">
            <Row className="g-4">
              <Col sm={3}>
                <div className="dashboard-wrap dashboard-menu-profile-main">
                  <div className="dashboard-menu-profile">
                    <div className="dashboard-menu-profile-img text-center">
                      <img
                        src={profileImage}
                        alt="User Avatar"
                        className="rounded-circle"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          opacity: imageLoading ? 0.6 : 1,
                        }}
                      />

                      <div className="pic-input-file">
                        <span>
                          {imageLoading ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            <i className="fa-solid fa-camera"></i>
                          )}
                        </span>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          disabled={imageLoading}
                        />
                      </div>
                    </div>

                    <div className="dashboard-menu-profile-text text-center mt-3">
                      <h4>
                        {user?.firstName || ""} {user?.lastName || ""}
                      </h4>
                    </div>
                  </div>

                  <Nav variant="pills" className="flex-column mt-3">
                    <Nav.Item>
                      <Nav.Link eventKey="profile">
                        <i className="fa-solid fa-user me-2"></i>
                        Edit Profile
                      </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                      <Nav.Link eventKey="orders">
                        <i className="fa-solid fa-list me-2"></i>
                        Your Orders
                      </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                      <Nav.Link eventKey="password">
                        <i className="fa-solid fa-lock me-2"></i>
                        Change Password
                      </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                      <Button
                        variant="danger"
                        className="w-100 dashboard-logout-btn"
                        onClick={() => setShowLogoutModal(true)}
                      >
                        <i className="fa-solid fa-right-from-bracket me-2"></i>
                        Logout
                      </Button>
                    </Nav.Item>
                  </Nav>
                </div>
              </Col>

              <Col sm={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="profile">
                    <div className="profile-form-wrapper">
                      <Form onSubmit={handleProfileSubmit}>
                        {message.text && (
                          <Alert variant={message.type}>{message.text}</Alert>
                        )}

                        <div className="profile-section mb-5">
                          <h2 className="profile-section-title">
                            Account Details
                          </h2>

                          <Row className="g-4">
                            <Col md={6}>
                              <label className="field-label">
                                Mobile Number
                              </label>

                              <div className="phone-picker-wrapper">
                                <PhoneInput
                                  defaultCountry="in"
                                  value={form.phoneNumber}
                                  onChange={(value) =>
                                    setForm((prev) => ({
                                      ...prev,
                                      phoneNumber: value,
                                    }))
                                  }
                                  inputProps={{
                                    name: "phoneNumber",
                                    required: true,
                                    placeholder: "Mobile Number",
                                  }}
                                />

                                <span className="verified-icon phone-verified-icon">
                                  <i className="fa-solid fa-check"></i>
                                </span>
                              </div>
                            </Col>

                            <Col md={6}>
                              <label className="field-label">
                                Email Address
                              </label>

                              <Form.Control
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                className="custom-profile-input"
                                placeholder="Get tickets on Email"
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="profile-section mb-5">
                          <h2 className="profile-section-title">
                            Personal Details
                          </h2>

                          <Row className="g-4">
                            <Col md={6}>
                              <label className="field-label">
                                First Name{" "}
                                <span className="required-star">*</span>
                              </label>

                              <Form.Control
                                type="text"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                                className="custom-profile-input"
                                placeholder="First Name"
                              />
                            </Col>

                            <Col md={6}>
                              <label className="field-label">
                                Last Name{" "}
                                <span className="required-star">*</span>
                              </label>

                              <Form.Control
                                type="text"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                                className="custom-profile-input"
                                placeholder="Last Name"
                              />
                            </Col>

                            <Col md={6}>
                              <label className="field-label">
                                Birthday (Optional)
                              </label>

                              <Form.Control
                                type="date"
                                name="birthday"
                                value={form.birthday}
                                onChange={handleChange}
                                className="custom-profile-input"
                              />
                            </Col>

                            <Col md={6}>
                              <label className="field-label">
                                Gender (Optional)
                              </label>

                              <div className="toggle-btn-group">
                                <Button
                                  type="button"
                                  className={`toggle-btn ${
                                    form.identity === "woman" ? "active" : ""
                                  }`}
                                  onClick={() =>
                                    handleSelect("identity", "woman")
                                  }
                                >
                                  Woman
                                </Button>

                                <Button
                                  type="button"
                                  className={`toggle-btn ${
                                    form.identity === "man" ? "active" : ""
                                  }`}
                                  onClick={() =>
                                    handleSelect("identity", "man")
                                  }
                                >
                                  Man
                                </Button>
                              </div>
                            </Col>

                            <Col md={6}>
                              <label className="field-label">
                                Married ? (Optional)
                              </label>

                              <div className="toggle-btn-group">
                                <Button
                                  type="button"
                                  className={`toggle-btn ${
                                    form.married === "yes" ? "active" : ""
                                  }`}
                                  onClick={() => handleSelect("married", "yes")}
                                >
                                  Yes
                                </Button>

                                <Button
                                  type="button"
                                  className={`toggle-btn ${
                                    form.married === "no" ? "active" : ""
                                  }`}
                                  onClick={() => handleSelect("married", "no")}
                                >
                                  No
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        <div className="profile-section">
                          <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
                            <h2 className="profile-section-title mb-0">
                              Saved Addresses
                            </h2>

                            <Button
                              type="button"
                              className="new-address-btn"
                              onClick={handleOpenAddressModal}
                            >
                              <i className="fa-solid fa-plus me-2"></i>
                              {savedAddress ? "Edit Address" : "New Address"}
                            </Button>
                          </div>

                          {savedAddress ? (
                            <div className="saved-address-card">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <span className="saved-address-badge">
                                    {savedAddress.addressType || "home"}
                                  </span>

                                  <p className="saved-address-text mt-2 mb-1">
                                    {savedAddress.addressLine1}
                                    {savedAddress.addressLine2
                                      ? `, ${savedAddress.addressLine2}`
                                      : ""}
                                  </p>

                                  <p className="saved-address-text mb-1">
                                    {savedAddress.landmark
                                      ? `${savedAddress.landmark}, `
                                      : ""}
                                    {savedAddress.city}, {savedAddress.state} -{" "}
                                    {savedAddress.pinCode}
                                  </p>
                                </div>

                                <div className="d-flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleOpenAddressModal}
                                  >
                                    Edit
                                  </Button>

                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleDeleteAddress}
                                    disabled={addressLoading}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted small">
                              No saved address found.
                            </div>
                          )}
                        </div>

                        <div className="mt-4">
                          <Button
                            type="submit"
                            className="save-profile-btn"
                            disabled={profileLoading}
                          >
                            {profileLoading ? (
                              <>
                                <Spinner
                                  size="sm"
                                  animation="border"
                                  className="me-2"
                                />
                                Saving...
                              </>
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </Tab.Pane>

                  <Tab.Pane eventKey="orders">
                    <OrderList />
                  </Tab.Pane>

                  <Tab.Pane eventKey="password">
                    <ChangePassword />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </div>

        <Modal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          centered
          size="lg"
          className="address-modal"
        >
          <Form onSubmit={handleAddressSubmit}>
            <Modal.Body className="address-modal-body">
              {addressMessage.text && (
                <Alert variant={addressMessage.type}>
                  {addressMessage.text}
                </Alert>
              )}

              <div className="address-type-tabs mb-3">
                <Button
                  type="button"
                  className={`address-type-btn ${
                    addressType === "home" ? "active" : ""
                  }`}
                  onClick={() => setAddressType("home")}
                >
                  Home
                </Button>

                <Button
                  type="button"
                  className={`address-type-btn ${
                    addressType === "work" ? "active" : ""
                  }`}
                  onClick={() => setAddressType("work")}
                >
                  Work
                </Button>

                <Button
                  type="button"
                  className={`address-type-btn ${
                    addressType === "other" ? "active" : ""
                  }`}
                  onClick={() => setAddressType("other")}
                >
                  Other
                </Button>
              </div>

              <Row className="g-4">
                <Col md={12}>
                  <label className="field-label">
                    Address Line 1 <span className="required-star">*</span>
                  </label>

                  <Form.Control
                    type="text"
                    name="addressLine1"
                    value={addressForm.addressLine1}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter Address Line 1"
                    required
                  />
                </Col>

                <Col md={12}>
                  <label className="field-label">Address Line 2</label>

                  <Form.Control
                    type="text"
                    name="addressLine2"
                    value={addressForm.addressLine2}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter Address Line 2"
                  />
                </Col>

                <Col md={6}>
                  <label className="field-label">Landmark</label>

                  <Form.Control
                    type="text"
                    name="landmark"
                    value={addressForm.landmark}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter Landmark"
                  />
                </Col>

                <Col md={6}>
                  <label className="field-label">
                    Pin Code <span className="required-star">*</span>
                  </label>

                  <Form.Control
                    type="text"
                    name="pinCode"
                    value={addressForm.pinCode}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter Pin Code"
                    required
                  />
                </Col>

                <Col md={6}>
                  <label className="field-label">
                    City <span className="required-star">*</span>
                  </label>

                  <Form.Control
                    type="text"
                    name="city"
                    value={addressForm.city}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter City"
                    required
                  />
                </Col>

                <Col md={6}>
                  <label className="field-label">
                    State <span className="required-star">*</span>
                  </label>

                  <Form.Control
                    type="text"
                    name="state"
                    value={addressForm.state}
                    onChange={handleAddressChange}
                    className="custom-profile-input"
                    placeholder="Enter State"
                    required
                  />
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer className="address-modal-footer">
              <Button
                type="button"
                variant="outline-danger"
                className="address-cancel-btn"
                onClick={() => setShowAddressModal(false)}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                className="address-save-btn"
                disabled={addressLoading}
              >
                {addressLoading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)}>
          <Modal.Body className="text-center">
            <i className="fa-solid fa-right-from-bracket fa-3x mb-3"></i>

            <h5>Are you sure you want to logout?</h5>

            <div className="d-flex justify-content-center gap-3 mt-3">
              <Button
                variant="secondary"
                onClick={() => setShowLogoutModal(false)}
              >
                No
              </Button>

              <Button variant="danger" onClick={handleLogoutFunction}>
                Yes
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </section>

      <Footer />
    </>
  );
};

export default Dashboard;
